export type Bonus = {
  number: number;
  title: string;
  value: string;
  image: string;
  headline: string;
  intro: string[];
  receive: string[];
  why: string;
  note?: string;
};

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: "Hemodynamic Monitoring Quick-Reference Cards",
    value: "$17",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/5c0f04c2-d319-4e96-ad58-91359aa9cb4b.jpg",
    headline: "Review essential hemodynamic concepts in a concise visual format.",
    intro: ["These educational reference cards organize selected monitoring concepts into easy-to-navigate summaries."],
    receive: [
      "Hemodynamic terminology cards",
      "MAP, CO, CI, CVP, and SVR concept summaries",
      "Preload and afterload comparison cards",
      "Cardiac performance reference diagrams",
      "Tissue perfusion concept cards",
      "Measurement limitation reminders",
      "Important definitions and abbreviations",
      "Visual reference layouts",
    ],
    why: "Keep essential study concepts organized in a concise format that complements the main volumes.",
  },
  {
    number: 2,
    title: "The 15-Minute Hemodynamic Study Review",
    value: "$12",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/7cb5037a-dc4f-4798-85c0-71836eb493b2.jpg",
    headline: "Make your daily study sessions more focused.",
    intro: [
      "You don’t always need another long chapter. Sometimes you need a consistent structure for reviewing one concept at a time.",
      "This resource provides a repeatable 15-minute educational study format.",
    ],
    receive: [
      "Daily topic selection worksheet",
      "Key concept review prompts",
      "Hemodynamic terminology questions",
      "Cardiovascular physiology checkpoints",
      "Parameter comparison exercises",
      "Personal study notes template",
      "Follow-up learning checklist",
      "Weekly study organization pages",
    ],
    why: "Use a consistent study structure to review what you’ve learned and identify concepts requiring further attention.",
    note: "The 15-minute format refers to an educational study session, not a complete patient assessment.",
  },
  {
    number: 3,
    title: "The Hemodynamic Interpretation Mistakes Library",
    value: "$17",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/1c3d0840-c222-46b2-85f1-760149962c23.jpg",
    headline: "Understand common misconceptions when studying hemodynamic monitoring.",
    intro: [
      "Interpreting hemodynamic information requires more than memorizing individual measurements.",
      "This resource explores common conceptual mistakes and explains why physiological context matters.",
    ],
    receive: [
      "Common misconceptions about MAP",
      "Cardiac output interpretation pitfalls",
      "CVP interpretation limitations",
      "Confusion between preload and fluid responsiveness",
      "Measurement variability examples",
      "Misconceptions about vascular resistance",
      "Cases involving conflicting monitoring findings",
      "Guided educational reflection exercises",
    ],
    why: "Develop a more careful approach to studying monitoring concepts and recognizing the limitations of individual measurements.",
  },
  {
    number: 4,
    title: "The Hemodynamic Clinical Case Practice Pack",
    value: "$17",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/f8ba9428-4a3d-431c-b7bf-b04bca8fb1df.jpg",
    headline: "Practice organizing hemodynamic information through simulated cases.",
    intro: ["This additional collection gives you more opportunities to study the concepts covered in Volume VI."],
    receive: [
      "Simulated patient presentation summaries",
      "Selected hemodynamic monitoring information",
      "Cardiovascular physiology scenarios",
      "Cardiac output concept exercises",
      "Tissue perfusion educational cases",
      "Cases involving multiple parameters",
      "Guided reasoning worksheets",
      "Suggested educational explanations",
    ],
    why: "Complement the main casebook with additional opportunities to organize information and explain your reasoning.",
  },
  {
    number: 5,
    title: "The Hemodynamic Monitoring Study Checklist Collection",
    value: "$12",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/a8ecba8a-1c21-46be-9fde-3754bb17d2a9.jpg",
    headline: "Organize your hemodynamic education with structured checklists.",
    intro: ["This collection provides educational checklists for reviewing selected hemodynamic monitoring topics."],
    receive: [
      "Cardiovascular physiology study checklist",
      "Hemodynamic parameter review checklist",
      "Preload, afterload, and contractility review",
      "Advanced monitoring terminology checklist",
      "Tissue perfusion concept checklist",
      "Measurement limitations review",
      "Clinical reasoning study prompts",
      "Personal learning-gap checklist",
    ],
    why: "Use structured prompts to review your educational material and identify topics requiring further study.",
    note: "These checklists are educational references, not approved bedside protocols.",
  },
];
